import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { styled, alpha } from '@mui/material/styles';
import { 
  Box, 
  Typography, 
  Button, 
  IconButton,
  Collapse,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  ExpandMore,
  Refresh,
  Send,
  Download,
  ContentCopy,
  BugReport,
  Support,
  ErrorOutline,
  ChevronRight
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { designTokens } from '../../theme/GlassmorphismDesignSystem';

// ============================================================================
// DESIGN TOKENS (Local reference for this component)
// ============================================================================
const tokens = {
  radius: {
    card: '18px',
    button: '10px',
    chip: '9999px',
    code: '12px',
  },
  borders: {
    light: {
      hairline: 'rgba(0, 0, 0, 0.04)',
      subtle: 'rgba(0, 0, 0, 0.06)',
    },
    dark: {
      hairline: 'rgba(255, 255, 255, 0.04)',
      subtle: 'rgba(255, 255, 255, 0.06)',
    },
  },
  shadows: {
    card: '0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.03)',
    cardDark: '0 1px 3px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  surfaces: {
    light: {
      container: 'rgba(255, 255, 255, 0.65)',
      card: 'rgba(255, 255, 255, 0.5)',
      codeBlock: 'rgba(0, 0, 0, 0.02)',
    },
    dark: {
      container: 'rgba(22, 22, 26, 0.7)',
      card: 'rgba(32, 32, 38, 0.55)',
      codeBlock: 'rgba(0, 0, 0, 0.25)',
    },
  },
  error: {
    bgLight: 'rgba(239, 68, 68, 0.06)',
    bgDark: 'rgba(239, 68, 68, 0.08)',
    borderLight: 'rgba(239, 68, 68, 0.12)',
    borderDark: 'rgba(239, 68, 68, 0.15)',
    text: '#EF4444',
  },
};

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const ControlButtonContainer = styled(Box)({
  position: 'absolute',
  top: '12px',
  right: '16px',
  zIndex: 1000,
  display: 'flex',
  gap: '6px',
});

const ControlButton = styled('div')(({ theme, color }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 150ms ease',
  backgroundColor: color,
  '&:hover': {
    transform: 'scale(1.15)',
  },
  '& .MuiSvgIcon-root': {
    visibility: 'hidden',
    fontSize: '8px',
    color: 'rgba(0,0,0,0.6)',
  },
  '&:hover .MuiSvgIcon-root': {
    visibility: 'visible',
  },
}));

const PageContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark' ? '#0A0A0C' : '#F8F9FA',
  minHeight: '100vh',
  padding: theme.spacing(3),
  position: 'relative',
}));

const MainGlassContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? tokens.surfaces.dark.container 
    : tokens.surfaces.light.container,
  backdropFilter: 'blur(24px) saturate(150%)',
  WebkitBackdropFilter: 'blur(24px) saturate(150%)',
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? tokens.borders.dark.subtle 
    : tokens.borders.light.subtle}`,
  borderRadius: tokens.radius.card,
  boxShadow: theme.palette.mode === 'dark' 
    ? tokens.shadows.cardDark 
    : tokens.shadows.card,
  padding: theme.spacing(3),
  maxWidth: '900px',
  height:'80vh',
  overflow:'scroll',
  margin: '0 auto',
  marginTop: theme.spacing(4),
}));

const GlassCard = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? tokens.surfaces.dark.card 
    : tokens.surfaces.light.card,
  backdropFilter: 'blur(12px) saturate(140%)',
  WebkitBackdropFilter: 'blur(12px) saturate(140%)',
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? tokens.borders.dark.hairline 
    : tokens.borders.light.hairline}`,
  borderRadius: tokens.radius.card,
  padding: theme.spacing(2.5),
  transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
}));

const ErrorCard = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? tokens.error.bgDark 
    : tokens.error.bgLight,
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? tokens.error.borderDark 
    : tokens.error.borderLight}`,
  borderRadius: tokens.radius.card,
  padding: theme.spacing(2.5),
}));

const Badge = styled(Box)(({ theme, variant = 'default' }) => {
  const variants = {
    error: {
      bg: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.08)',
      border: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)',
      color: tokens.error.text,
    },
    default: {
      bg: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
      border: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
      color: theme.palette.text.secondary,
    },
  };
  const v = variants[variant] || variants.default;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: v.bg,
    border: `1px solid ${v.border}`,
    borderRadius: tokens.radius.chip,
    padding: '4px 12px',
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.02em',
    color: v.color,
  };
});

const PrimaryButton = styled(Button)(({ theme, accentColor }) => {
  const color = accentColor || theme.palette.primary.main;
  return {
    background: color,
    color: '#fff',
    borderRadius: tokens.radius.button,
    padding: '10px 20px',
    fontWeight: 600,
    fontSize: '13px',
    textTransform: 'none',
    boxShadow: `0 2px 8px ${alpha(color, 0.25)}`,
    transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: alpha(color, 0.9),
      boxShadow: `0 4px 12px ${alpha(color, 0.35)}`,
      transform: 'translateY(-1px)',
    },
    '&:focus-visible': {
      boxShadow: `0 0 0 3px ${alpha(color, 0.25)}`,
    },
  };
});

const SecondaryButton = styled(Button)(({ theme }) => ({
  background: 'transparent',
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? tokens.borders.dark.subtle 
    : tokens.borders.light.subtle}`,
  borderRadius: tokens.radius.button,
  padding: '9px 18px',
  fontWeight: 500,
  fontSize: '13px',
  textTransform: 'none',
  backdropFilter: 'blur(8px)',
  transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.06)' 
      : 'rgba(0, 0, 0, 0.04)',
    borderColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.12)' 
      : 'rgba(0, 0, 0, 0.1)',
  },
  '&:focus-visible': {
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 0 0 2px rgba(255, 255, 255, 0.08)' 
      : '0 0 0 2px rgba(0, 0, 0, 0.05)',
  },
}));

const GhostButton = styled(Button)(({ theme }) => ({
  background: 'transparent',
  color: theme.palette.text.secondary,
  borderRadius: tokens.radius.button,
  padding: '8px 14px',
  fontWeight: 500,
  fontSize: '12px',
  textTransform: 'none',
  minWidth: 'auto',
  transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.04)' 
      : 'rgba(0, 0, 0, 0.03)',
    color: theme.palette.text.primary,
  },
}));

const CodeBlock = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? tokens.surfaces.dark.codeBlock 
    : tokens.surfaces.light.codeBlock,
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? tokens.borders.dark.hairline 
    : tokens.borders.light.hairline}`,
  borderRadius: tokens.radius.code,
  padding: theme.spacing(2),
  fontSize: '12px',
  fontFamily: "'SF Mono', Monaco, Menlo, 'Ubuntu Mono', monospace",
  overflow: 'auto',
  maxHeight: '300px',
  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.8)',
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  '&::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
    borderRadius: '3px',
  },
}));

const AccordionHeader = styled(Box)(({ theme, expanded }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5, 2),
  cursor: 'pointer',
  borderRadius: expanded ? `${tokens.radius.code} ${tokens.radius.code} 0 0` : tokens.radius.code,
  background: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.03)' 
    : 'rgba(0, 0, 0, 0.02)',
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? tokens.borders.dark.hairline 
    : tokens.borders.light.hairline}`,
  borderBottom: expanded ? 'none' : undefined,
  transition: 'all 150ms ease',
  '&:hover': {
    background: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(0, 0, 0, 0.03)',
  },
}));

const AccordionContent = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? tokens.borders.dark.hairline 
    : tokens.borders.light.hairline}`,
  borderTop: 'none',
  borderRadius: `0 0 ${tokens.radius.code} ${tokens.radius.code}`,
  overflow: 'hidden',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  letterSpacing: '-0.01em',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
}));

const CardGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

const DebugPage = ({ error, errorInfo, onRetry, onRefresh }) => {
  const [debugInfo, setDebugInfo] = useState({});
  const [showTechnical, setShowTechnical] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [stackExpanded, setStackExpanded] = useState(false);
  const [copiedStack, setCopiedStack] = useState(false);
  const businessData = useSelector((state) => state.core.businessData);
  const themeMode = useSelector((state) => state.core.themeMode);
  const user = useSelector((state) => state.core.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const collectDebugInfo = () => {
      const info = {
        timestamp: new Date().toISOString(),
        browser: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
          cookieEnabled: navigator.cookieEnabled,
          onLine: navigator.onLine
        },
        page: {
          url: window.location.href,
          path: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash
        },
        storage: {
          localStorage: Object.keys(localStorage).reduce((acc, key) => {
            if (key.includes('theme') || key.includes('user') || key.includes('business')) {
              acc[key] = localStorage.getItem(key);
            }
            return acc;
          }, {}),
          sessionStorage: Object.keys(sessionStorage)
        },
        redux: {
          businessData: businessData,
          themeMode: themeMode,
          user: user ? {
            uid: user.uid,
            email: user.email,
            theme: user.theme,
            lang: user.lang
          } : null
        },
        performance: {
          memory: performance.memory ? {
            usedJSHeapSize: performance.memory.usedJSHeapSize,
            totalJSHeapSize: performance.memory.totalJSHeapSize
          } : 'Non disponible',
          timing: performance.timing ? {
            loadTime: performance.timing.loadEventEnd - performance.timing.navigationStart,
            domReady: performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart
          } : 'Non disponible'
        },
        errors: {
          current: error ? {
            message: error.message,
            stack: error.stack,
            name: error.name
          } : null,
          errorInfo: errorInfo ? {
            componentStack: errorInfo.componentStack
          } : null
        }
      };

      setDebugInfo(info);
    };

    collectDebugInfo();
  }, [error, errorInfo, businessData, themeMode, user]);

  const copyToClipboard = () => {
    const debugText = JSON.stringify(debugInfo, null, 2);
    navigator.clipboard.writeText(debugText).then(() => {
      alert('Informations de debug copiées dans le presse-papiers !');
    });
  };

  const copyStackTrace = () => {
    if (error?.stack) {
      navigator.clipboard.writeText(error.stack).then(() => {
        setCopiedStack(true);
        setTimeout(() => setCopiedStack(false), 2000);
      });
    }
  };

  const downloadDebugInfo = () => {
    const debugText = JSON.stringify(debugInfo, null, 2);
    const blob = new Blob([debugText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-${new Date().toISOString().slice(0, 19)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFullscreen = () => {
    const elem = document.documentElement;

    const isFullscreenActive = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement ||
      document.mozFullScreenElement
    );

    if (!isFullscreenActive) {
      const requestFullscreen =
        elem.requestFullscreen ||
        elem.webkitRequestFullscreen ||
        elem.msRequestFullscreen ||
        elem.mozRequestFullScreen;

      if (requestFullscreen) {
        requestFullscreen.call(elem).catch((err) => {
          console.error(
            `Error attempting to enable full-screen mode: ${err.message} (${err.name})`
          );
        });
      }
    } else {
      const exitFullscreen =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.msExitFullscreen ||
        document.mozCancelFullScreen;

      if (exitFullscreen) {
        exitFullscreen.call(document);
      }
    }
  };

  const handleClose = () => {
    window.history.back();
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenActive = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement ||
        document.mozFullScreenElement
      );
      setIsFullscreen(isFullscreenActive);
    };

    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'msfullscreenchange',
      'mozfullscreenchange',
    ];
    events.forEach((event) => {
      document.addEventListener(event, handleFullscreenChange);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleFullscreenChange);
      });
    };
  }, []);

  const accentColor = businessData?.mainColor || theme.palette.primary.main;

  return (
    <PageContainer>
      {/* Control buttons */}
      <ControlButtonContainer>
        <ControlButton
          color={businessData?.secColor || theme.palette.secondary.main}
          onClick={handleFullscreen}
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </ControlButton>
        <ControlButton
          color={accentColor}
          onClick={handleClose}
        >
          <CloseIcon />
        </ControlButton>
      </ControlButtonContainer>

      <MainGlassContainer>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box 
            sx={{ 
              width: 48, 
              height: 48, 
              borderRadius: '14px',
              background: alpha(accentColor, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 2,
            }}
          >
            <BugReport sx={{ fontSize: 24, color: accentColor }} />
          </Box>
          <Typography 
            sx={{ 
              fontSize: '20px', 
              fontWeight: 600, 
              color: 'text.primary',
              letterSpacing: '-0.02em',
              mb: 0.5,
            }}
          >
            Page de Debug Technique
          </Typography>
          <Typography 
            sx={{ 
              fontSize: '13px', 
              color: 'text.secondary',
              mb: 2,
            }}
          >
            {debugInfo.timestamp && new Date(debugInfo.timestamp).toLocaleString('fr-FR')}
          </Typography>
        </Box>

        {/* Error Card (if error exists) */}
        {error && (
          <ErrorCard sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ErrorOutline sx={{ fontSize: 18, color: tokens.error.text }} />
                <Badge variant="error">Erreur détectée</Badge>
              </Box>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography 
                sx={{ 
                  fontSize: '13px', 
                  fontWeight: 600, 
                  color: tokens.error.text,
                  mb: 0.5,
                }}
              >
                {error.name}
              </Typography>
              <Typography 
                sx={{ 
                  fontSize: '13px', 
                  color: 'text.primary',
                  lineHeight: 1.5,
                }}
              >
                {error.message}
              </Typography>
            </Box>

            {/* Stack trace accordion */}
            {error.stack && (
              <Box sx={{ mt: 2 }}>
                <AccordionHeader 
                  expanded={stackExpanded} 
                  onClick={() => setStackExpanded(!stackExpanded)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChevronRight 
                      sx={{ 
                        fontSize: 16, 
                        color: 'text.secondary',
                        transform: stackExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 150ms ease',
                      }} 
                    />
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'text.secondary' }}>
                      Stack trace complet
                    </Typography>
                  </Box>
                  <GhostButton 
                    onClick={(e) => { e.stopPropagation(); copyStackTrace(); }}
                    startIcon={<ContentCopy sx={{ fontSize: 14 }} />}
                    size="small"
                  >
                    {copiedStack ? 'Copié !' : 'Copier'}
                  </GhostButton>
                </AccordionHeader>
                <Collapse in={stackExpanded}>
                  <AccordionContent>
                    <CodeBlock>
                      {error.stack}
                    </CodeBlock>
                  </AccordionContent>
                </Collapse>
              </Box>
            )}
          </ErrorCard>
        )}

        {/* Actions Card */}
        <GlassCard sx={{ mb: 3 }}>
          <SectionTitle sx={{ mb: 2 }}>
            Actions
          </SectionTitle>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <PrimaryButton 
              onClick={onRetry} 
              accentColor={accentColor}
              startIcon={<Send sx={{ fontSize: 16 }} />}
            >
              Réessayer
            </PrimaryButton>
            <SecondaryButton 
              onClick={onRefresh} 
              startIcon={<Refresh sx={{ fontSize: 16 }} />}
            >
              Rafraîchir
            </SecondaryButton>
            <GhostButton 
              onClick={copyToClipboard} 
              startIcon={<ContentCopy sx={{ fontSize: 14 }} />}
            >
              Copier
            </GhostButton>
            <GhostButton 
              onClick={downloadDebugInfo} 
              startIcon={<Download sx={{ fontSize: 14 }} />}
            >
              Télécharger
            </GhostButton>
            <SecondaryButton 
              onClick={() => setShowTechnical(!showTechnical)} 
              endIcon={
                <ChevronRight 
                  sx={{ 
                    fontSize: 16,
                    transform: showTechnical ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 150ms ease',
                  }} 
                />
              }
            >
              Voir détails
            </SecondaryButton>
          </Box>
        </GlassCard>

        {/* 2-column grid: Support + Technical details toggle */}
        <CardGrid>
          {/* Support Card */}
          <GlassCard>
            <SectionTitle sx={{ mb: 2 }}>
              <Support sx={{ fontSize: 18, color: accentColor }} />
              Support technique
            </SectionTitle>
            <Box component="ol" sx={{ pl: 2.5, m: 0, color: 'text.secondary' }}>
              <Typography component="li" sx={{ fontSize: '13px', mb: 1, lineHeight: 1.6 }}>
                Cliquez sur <strong>"Copier"</strong> ou <strong>"Télécharger"</strong>
              </Typography>
              <Typography component="li" sx={{ fontSize: '13px', mb: 1, lineHeight: 1.6 }}>
                Envoyez un courriel à <strong>support@usenode.com</strong>
              </Typography>
              <Typography component="li" sx={{ fontSize: '13px', lineHeight: 1.6 }}>
                Décrivez ce que vous faisiez
              </Typography>
            </Box>
          </GlassCard>

          {/* Résumé Card */}
          <GlassCard>
            <SectionTitle sx={{ mb: 2 }}>
              Résumé
            </SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Statut</Typography>
                <Badge variant={error ? 'error' : 'default'}>
                  {error ? 'Erreur' : 'OK'}
                </Badge>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Navigateur</Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.primary', fontWeight: 500 }}>
                  {navigator.onLine ? 'En ligne' : 'Hors ligne'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Page</Typography>
                <Typography 
                  sx={{ 
                    fontSize: '12px', 
                    color: 'text.primary', 
                    fontWeight: 500,
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {window.location.pathname}
                </Typography>
              </Box>
            </Box>
          </GlassCard>
        </CardGrid>

        {/* Technical Details (expanded) */}
        <Collapse in={showTechnical}>
          <GlassCard sx={{ mt: 3 }}>
            <SectionTitle sx={{ mb: 2 }}>
              Détails techniques complets
            </SectionTitle>
            
            <CardGrid sx={{ mb: 2 }}>
              {/* Browser Information */}
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'text.secondary', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Navigateur & Système
                </Typography>
                <CodeBlock sx={{ maxHeight: '150px' }}>
                  {JSON.stringify(debugInfo.browser, null, 2)}
                </CodeBlock>
              </Box>

              {/* Page Information */}
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'text.secondary', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Information page
                </Typography>
                <CodeBlock sx={{ maxHeight: '150px' }}>
                  {JSON.stringify(debugInfo.page, null, 2)}
                </CodeBlock>
              </Box>

              {/* Storage Information */}
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'text.secondary', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Stockage local
                </Typography>
                <CodeBlock sx={{ maxHeight: '150px' }}>
                  {JSON.stringify(debugInfo.storage, null, 2)}
                </CodeBlock>
              </Box>

              {/* Redux State */}
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'text.secondary', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  État Redux
                </Typography>
                <CodeBlock sx={{ maxHeight: '150px' }}>
                  {JSON.stringify(debugInfo.redux, null, 2)}
                </CodeBlock>
              </Box>
            </CardGrid>

            {/* Full JSON */}
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'text.secondary', mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Informations complètes (JSON)
              </Typography>
              <CodeBlock>
                {JSON.stringify(debugInfo, null, 2)}
              </CodeBlock>
            </Box>
          </GlassCard>
        </Collapse>
      </MainGlassContainer>
    </PageContainer>
  );
};

export default DebugPage;
